import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import ProductCard from '../components/ProductCard';
import SEO from '../components/SEO';
import { repository } from '../lib/repository';

const HERO_IMG = 'https://images.unsplash.com/photo-1549298222-1c31e8915347?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400';
const SPLIT_IMG = 'https://images.unsplash.com/photo-1554631830-92b02aafbfcd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600';
const JOURNAL_IMG = 'https://images.unsplash.com/photo-1483985988355-763728e1935b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600';

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [categories, setCategories] = useState([]);
  const [journal, setJournal] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    repository.listFeatured(8).then(setFeatured);
    repository.listCategories().then(setCategories);
    repository.listJournal().then((j) => setJournal(j.slice(0, 3)));
    repository.listTestimonials().then(setTestimonials);
  }, []);

  // One card per department, with the first matching category's image as the visual.
  const departments = (() => {
    const dTags = {
      men: 'Considered essentials for every day.',
      women: 'Quiet confidence, tailored.',
      kids: 'Small pieces, made to last.',
      accessories: 'The mark of a considered detail.',
    };
    const seen = new Set();
    const out = [];
    for (const c of categories) {
      if (seen.has(c.department)) continue;
      seen.add(c.department);
      out.push({
        slug: c.department,
        name: c.department.charAt(0).toUpperCase() + c.department.slice(1),
        tagline: dTags[c.department] || '',
        image: c.image,
      });
    }
    return out;
  })();

  return (
    <div data-testid="page-home">
      <SEO
        description="Considered clothing for every day — men, women, kids, accessories. Designed for everyday confidence."
        path="/"
        keywords={['luxury clothing','minimal fashion','considered clothing','wardrobe essentials','cashmere','merino','sustainable clothing']}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'Evermark',
          url: 'https://evermark.com',
          potentialAction: {
            '@type': 'SearchAction',
            target: 'https://evermark.com/shop?q={search_term_string}',
            'query-input': 'required name=search_term_string',
          },
        }}
      />
      {/* Marquee */}
      <div className="border-b border-border overflow-hidden">
        <div className="flex whitespace-nowrap animate-marquee py-3 gap-16 overline text-muted-foreground">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex gap-16 shrink-0">
              <span>Winter Edition 2026</span><span>·</span>
              <span>Complimentary Shipping Over $150</span><span>·</span>
              <span>Made Slow. Made to Last.</span><span>·</span>
              <span>New Arrivals</span><span>·</span>
              <span>A Legacy in Every Mark</span><span>·</span>
              <span>Winter Edition 2026</span><span>·</span>
              <span>Complimentary Shipping Over $150</span><span>·</span>
              <span>Made Slow. Made to Last.</span><span>·</span>
              <span>New Arrivals</span><span>·</span>
              <span>A Legacy in Every Mark</span><span>·</span>
            </div>
          ))}
        </div>
      </div>

      {/* HERO — full-bleed lifestyle photo + centered wordmark */}
      <section data-testid="hero" className="relative w-full h-[92vh] min-h-[640px] overflow-hidden">
        <img
          src={HERO_IMG}
          srcSet={heroSrcSet(HERO_IMG)}
          sizes="100vw"
          alt="Evermark winter editorial"
          width="2400"
          height="1600"
          loading="eager"
          fetchpriority="high"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/30" />
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6 text-white">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.85, 0, 0.15, 1] }} className="overline">
            Winter Edition — 2026
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.15, ease: [0.85, 0, 0.15, 1] }}
            className="mt-6 font-display text-6xl md:text-8xl lg:text-9xl tracking-[0.02em] leading-[0.9]"
          >
            EVERMARK
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.35, ease: [0.85, 0, 0.15, 1] }}
            className="mt-6 font-display italic text-xl md:text-2xl max-w-xl"
          >
            Designed for Everyday Confidence.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.55, ease: [0.85, 0, 0.15, 1] }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <Button asChild size="lg" className="bg-white text-black hover:bg-white/85" data-testid="hero-cta-shop">
              <Link to="/shop">Shop the Edition</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-black" data-testid="hero-cta-lookbook">
              <Link to="/lookbook">View Lookbook</Link>
            </Button>
          </motion.div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 overline text-white/80">Scroll ↓</div>
      </section>

      {/* SPLIT EDITORIAL */}
      <section data-testid="split-editorial" className="container py-24 md:py-40 grid md:grid-cols-2 gap-12 md:gap-20 items-center">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.9, ease: [0.85, 0, 0.15, 1] }}
          className="aspect-[4/5] overflow-hidden bg-muted"
        >
          <img src={SPLIT_IMG} srcSet={heroSrcSet(SPLIT_IMG)} sizes="(max-width: 768px) 100vw, 50vw" alt="Merino crewneck editorial" width="1600" height="2000" loading="lazy" decoding="async" className="h-full w-full object-cover" />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.85, 0, 0.15, 1] }}
          className="md:pl-10"
        >
          <p className="overline text-muted-foreground">The Quiet Uniform</p>
          <h2 className="mt-6 font-display text-5xl md:text-7xl leading-[0.95] tracking-tight">
            Fewer pieces, <em className="italic font-light">worn well</em>.
          </h2>
          <p className="mt-8 text-lg text-muted-foreground max-w-md leading-relaxed">
            The wardrobe we return to is the one that grows quieter as we grow older. Merino spun in Biella. Linen soft to the touch. Cotton with weight in the hand.
          </p>
          <div className="mt-10 flex items-center gap-8">
            <Button asChild variant="outline" data-testid="split-cta">
              <Link to="/shop">Shop the Story</Link>
            </Button>
            <Link to="/journal/the-quiet-uniform" className="overline link-underline inline-flex items-center gap-2">
              Read the Journal <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </motion.div>
      </section>

      {/* CATEGORIES — one tile per department */}
      <section data-testid="category-grid" className="container pb-24 md:pb-40">
        <div className="mb-12 flex items-end justify-between">
          <div>
            <p className="overline text-muted-foreground">The Catalog</p>
            <h2 className="mt-3 font-display text-4xl md:text-5xl">Shop by Chapter</h2>
          </div>
          <Link to="/shop" className="hidden md:inline-flex overline link-underline items-center gap-2">
            All Pieces <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {departments.map((d, i) => (
            <motion.div
              key={d.slug}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: i * 0.1, ease: [0.85, 0, 0.15, 1] }}
            >
              <Link to={`/shop/${d.slug}`} className="group block kb-parent relative overflow-hidden aspect-[3/4] bg-muted" data-testid={`category-tile-${d.slug}`}>
                <img src={d.image} srcSet={heroSrcSet(d.image)} sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw" alt={d.name} loading="lazy" decoding="async" width="1400" height="1867" className="kb-img absolute inset-0 h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-8 text-white">
                  <p className="overline">{d.tagline}</p>
                  <h3 className="mt-2 font-display text-4xl md:text-5xl">{d.name}</h3>
                  <span className="mt-4 inline-flex items-center gap-2 overline link-reveal">Enter <ArrowRight className="h-3 w-3" /></span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* Fine-grained: shop by category (all 20) */}
        <div className="mt-16">
          <div className="mb-6 flex items-end justify-between">
            <p className="overline text-muted-foreground">Or by fine category</p>
            <Link to="/shop" className="overline link-underline">See all 20</Link>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="category-chips">
            {categories.map((c) => (
              <Link
                key={c.slug}
                to={`/shop/${c.slug}`}
                data-testid={`category-chip-${c.slug}`}
                className="text-xs px-3 py-1.5 border border-border hover:border-foreground transition-colors duration-500"
              >
                {c.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section data-testid="featured-products" className="container pb-24 md:pb-40">
        <div className="mb-12 flex items-end justify-between">
          <div>
            <p className="overline text-muted-foreground">This Season</p>
            <h2 className="mt-3 font-display text-4xl md:text-5xl">Featured Pieces</h2>
          </div>
          <Link to="/shop" className="overline link-underline">Shop all</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-12 md:gap-x-6">
          {featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </section>

      {/* JOURNAL PREVIEW */}
      <section data-testid="journal-preview" className="border-t border-border">
        <div className="container py-24 md:py-40 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <p className="overline text-muted-foreground">The Journal</p>
            <h2 className="mt-4 font-display text-5xl md:text-6xl leading-[0.95]">Letters from the studio.</h2>
            <p className="mt-6 text-muted-foreground max-w-sm leading-relaxed">Essays on cloth, craft, and the wardrobes we keep.</p>
            <Button asChild variant="outline" className="mt-8">
              <Link to="/journal">All Entries</Link>
            </Button>
          </div>
          <div className="md:col-span-8 grid gap-8">
            {journal.map((j, i) => (
              <motion.article
                key={j.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.1, ease: [0.85, 0, 0.15, 1] }}
              >
                <Link to={`/journal/${j.slug}`} className="grid md:grid-cols-3 gap-6 group items-center border-t border-border pt-8 first:border-t-0 first:pt-0">
                  <div className="aspect-[4/3] overflow-hidden md:col-span-1">
                    <img src={j.cover} srcSet={heroSrcSet(j.cover)} sizes="(max-width: 768px) 100vw, 33vw" alt={j.title} loading="lazy" decoding="async" width="1600" height="1200" className="kb-img h-full w-full object-cover" />
                  </div>
                  <div className="md:col-span-2">
                    <p className="overline text-muted-foreground">{new Date(j.date).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} · {j.readTime}</p>
                    <h3 className="mt-3 font-display text-3xl md:text-4xl leading-tight">{j.title}</h3>
                    <p className="mt-3 text-muted-foreground">{j.excerpt}</p>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section data-testid="testimonials" className="border-t border-border">
        <div className="container py-24 md:py-32">
          <div className="grid md:grid-cols-12 gap-12 items-end mb-14">
            <div className="md:col-span-6">
              <p className="overline text-muted-foreground">Read the room</p>
              <h2 className="mt-4 font-display text-5xl md:text-6xl leading-[0.95]">Considered. Kept. Repeated.</h2>
            </div>
            <p className="md:col-span-6 text-muted-foreground max-w-md md:justify-self-end">
              Notes from editors, architects, and the people who wear our pieces day after day.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-4 md:gap-6">
            {testimonials.slice(0, 6).map((t, i) => (
              <motion.figure
                key={t.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.7, delay: (i % 3) * 0.08, ease: [0.85, 0, 0.15, 1] }}
                className="border border-border p-8 hover:border-foreground transition-colors duration-500"
                data-testid={`testimonial-${t.id}`}
              >
                <div className="tabular text-foreground text-sm mb-4" aria-label={`${t.rating} out of 5`}>
                  {'★'.repeat(t.rating)}{'☆'.repeat(5 - t.rating)}
                </div>
                <blockquote className="font-display text-xl md:text-2xl leading-[1.25]">"{t.quote}"</blockquote>
                <figcaption className="mt-6 overline text-muted-foreground">
                  {t.author} <span className="opacity-60">· {t.role}</span>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      {/* MOUNTAIN QUOTE STRIP */}
      <section className="border-y border-border">
        <div className="container py-24 text-center">
          <p className="overline text-muted-foreground mb-8">A Legacy in Every Mark</p>
          <p className="font-display text-3xl md:text-5xl leading-[1.15] max-w-4xl mx-auto">
            <em className="italic font-light">"The mountain</em> in our mark is not a symbol of ascent — it is a study of <em className="italic font-light">steadiness</em>."
          </p>
        </div>
      </section>
    </div>
  );
}

function heroSrcSet(src) {
  if (!src || !src.includes('images.unsplash.com')) return undefined;
  const widths = [800, 1200, 1600, 2000, 2400, 3200];
  return widths.map((w) => {
    const next = src.includes('w=') ? src.replace(/w=\d+/, `w=${w}`) : src + (src.includes('?') ? '&' : '?') + `w=${w}`;
    return `${next} ${w}w`;
  }).join(', ');
}
